import type { LogoProvider } from '@/design-system';
import type {
  ApiKeyProvider,
  ApiKeyStatus,
  BedrockModelHealth,
  BedrockModelHealthEntry,
  ProbeResult,
} from '@/versions/v1/shared/admin-seed';
import { ADMIN_SEED_ANCHOR_MS } from '@/versions/v1/shared/admin-seed';

export const API_KEY_PROVIDERS = [
  'claude',
  'google',
  'voyage',
  'daytona',
  'bedrock',
  'bedrock-embeddings',
] as const;

export const PROVIDER_LABELS: Record<ApiKeyProvider, string> = {
  claude: 'Claude (Anthropic)',
  google: 'Google Gemini',
  voyage: 'Voyage (embeddings)',
  daytona: 'Daytona (sandbox)',
  bedrock: 'AWS Bedrock (bearer token)',
  'bedrock-embeddings': 'AWS Bedrock — embeddings',
};

export const PROVIDER_ENV_VARS: Record<ApiKeyProvider, readonly string[]> = {
  claude: ['ANTHROPIC_API_KEY'],
  google: ['GEMINI_API_KEY', 'GOOGLE_GENAI_API_KEY', 'GOOGLE_API_KEY'],
  voyage: ['VOYAGE_API_KEY'],
  daytona: ['DAYTONA_API_KEY'],
  bedrock: ['AWS_BEARER_TOKEN_BEDROCK'],
  'bedrock-embeddings': ['AWS_BEARER_TOKEN_BEDROCK_EMBEDDINGS'],
};

export const PROVIDER_SUPPORTS_PROBE: Record<ApiKeyProvider, boolean> = {
  claude: true,
  google: true,
  voyage: true,
  daytona: true,
  bedrock: true,
  'bedrock-embeddings': false,
};

export const SEGMENT_LABELS = {
  'anthropic-native': 'Anthropic API',
  'aws-bedrock': 'AWS Bedrock',
  'claude-platform-on-aws': 'Claude Platform on AWS',
} as const;

const MODEL_SHORT_LABELS: Record<string, string> = {
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-opus-4-8': 'Opus 4.8',
  'claude-haiku-4-5': 'Haiku 4.5',
};

export function formatRelative(epochMs: number, now = ADMIN_SEED_ANCHOR_MS): string {
  const delta = now - epochMs;
  const mins = Math.floor(delta / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return new Date(epochMs).toLocaleDateString('en-US', { timeZone: 'UTC' });
}

export function formatCheckedAt(epochMs: number): string {
  return formatRelative(epochMs);
}

export function maskedDbStatus(status: ApiKeyStatus): string {
  return `••••••••${status.last4 ?? ''}`;
}

export function probePill(result: ProbeResult): {
  label: string;
  tone: 'success' | 'danger' | 'default' | 'warning';
} {
  if (result.errorCode === 'PROPAGATING') {
    return { label: 'Propagating…', tone: 'warning' };
  }
  if (result.status === 'ok') {
    return { label: 'Connected', tone: 'success' };
  }
  if (result.status === 'error') {
    return { label: 'Failed', tone: 'danger' };
  }
  return { label: 'Unknown', tone: 'default' };
}

export function modelShortLabel(id: string): string {
  return MODEL_SHORT_LABELS[id] ?? id;
}

function isPropagatingModelEntry(m: BedrockModelHealthEntry): boolean {
  return m.errorCode === 'PROPAGATING';
}

export function unhealthyModelIds(h: BedrockModelHealth | null): Set<string> {
  return new Set(
    (h?.models ?? [])
      .filter((m) => m.status === 'error' && !isPropagatingModelEntry(m))
      .map((m) => m.modelId),
  );
}

export function aggregateBedrockHealth(
  h: BedrockModelHealth | null,
): 'ok' | 'degraded' | 'down' | 'unknown' {
  if (!h || h.models.length === 0) return 'unknown';
  const relevant = h.models.filter((m) => !isPropagatingModelEntry(m));
  if (relevant.length === 0) return 'unknown';
  const ok = relevant.filter((m) => m.status === 'ok').length;
  if (ok === relevant.length) return 'ok';
  if (ok === 0) return 'down';
  return 'degraded';
}

export function bedrockAggregatePill(
  aggregate: ReturnType<typeof aggregateBedrockHealth>,
): { label: string; tone: 'success' | 'danger' | 'default' | 'warning' } {
  switch (aggregate) {
    case 'ok':
      return { label: 'Connected', tone: 'success' };
    case 'degraded':
      return { label: 'Partial', tone: 'warning' };
    case 'down':
      return { label: 'Failed', tone: 'danger' };
    default:
      return { label: 'Unknown', tone: 'default' };
  }
}

export function formatBedrockDegradedNote(health: BedrockModelHealth): string {
  const unhealthy = unhealthyModelIds(health);
  const unavail = health.models
    .filter((m) => unhealthy.has(m.modelId))
    .map((m) => modelShortLabel(m.modelId));
  const avail = health.models
    .filter((m) => m.status === 'ok')
    .map((m) => modelShortLabel(m.modelId));
  const unavailPart =
    unavail.length === 1
      ? `${unavail[0]} not available`
      : `${unavail.join(', ')} not available`;
  const availPart =
    avail.length === 1
      ? `${avail[0]} working`
      : `${avail.join(', ')} working`;
  return `Note: ${unavailPart}; ${availPart}.`;
}

export function modelEntryPill(
  entry: BedrockModelHealthEntry,
): { label: string; tone: 'success' | 'danger' | 'warning' } {
  if (entry.status === 'ok') {
    return { label: 'ok', tone: 'success' };
  }
  if (entry.errorCode === 'PROPAGATING') {
    return { label: 'propagating…', tone: 'warning' };
  }
  return { label: 'error', tone: 'danger' };
}

export function apiKeyToLogoProvider(provider: ApiKeyProvider): LogoProvider {
  switch (provider) {
    case 'claude':
      return 'anthropic';
    case 'google':
      return 'google';
    case 'voyage':
      return 'voyage';
    case 'daytona':
      return 'daytona';
    case 'bedrock':
      return 'bedrock';
    case 'bedrock-embeddings':
      return 'bedrock';
  }
}

export type RowState = {
  expanded: boolean;
  keyInput: string;
  labelInput: string;
  busy: boolean;
  testing: boolean;
  error: string | null;
  confirmClear: boolean;
};

export type DaytonaUrlRowState = {
  expanded: boolean;
  urlInput: string;
  busy: boolean;
  error: string | null;
  confirmClear: boolean;
};

export function initialRowState(): RowState {
  return {
    expanded: false,
    keyInput: '',
    labelInput: '',
    busy: false,
    testing: false,
    error: null,
    confirmClear: false,
  };
}

export function initialDaytonaUrlRowState(): DaytonaUrlRowState {
  return {
    expanded: false,
    urlInput: '',
    busy: false,
    error: null,
    confirmClear: false,
  };
}

export function initialRowStateMap(): Record<ApiKeyProvider, RowState> {
  return Object.fromEntries(
    API_KEY_PROVIDERS.map((p) => [p, initialRowState()]),
  ) as Record<ApiKeyProvider, RowState>;
}

export function mockSaveApiKey(
  statuses: ApiKeyStatus[],
  provider: ApiKeyProvider,
  keyInput: string,
  labelInput: string,
): ApiKeyStatus[] {
  const last4 = keyInput.slice(-4);
  return statuses.map((s) =>
    s.provider === provider
      ? {
          provider,
          source: 'db',
          last4,
          label: labelInput.trim() || null,
          rotatedAt: ADMIN_SEED_ANCHOR_MS,
          setByEmail: 'alex@example.com',
        }
      : s,
  );
}

export function mockClearApiKey(
  statuses: ApiKeyStatus[],
  provider: ApiKeyProvider,
): ApiKeyStatus[] {
  return statuses.map((s) =>
    s.provider === provider
      ? {
          provider,
          source: 'none',
          last4: null,
          label: null,
          rotatedAt: null,
          setByEmail: null,
        }
      : s,
  );
}

export function mockProbeOk(_provider: ApiKeyProvider): ProbeResult {
  return {
    status: 'ok',
    checkedAt: ADMIN_SEED_ANCHOR_MS,
    errorCode: null,
    errorMessage: null,
  };
}
